import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { AuditPassportCellComponent } from './audit-passport-cell.component';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { PassportService, PassportRecord } from '../shared/passport.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { UiStateService } from '../shared/ui-state.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { PassportHelperService } from '../shared/passport-helper.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { BaseTabPersonaComponent } from '../shared/base-tab-persona.component';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { NoPassportsComponent } from '../shared/no-passports.component';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { HistoryPanelService } from '../shared/history-panel.service';
import { PassportsDesktopTableComponent } from '../shared/passports-desktop-table.component';
import {
  AuditListSnapshotBridgeService,
  clearAuditListRestoreState,
  normalizeAuditDesktopShowRows,
  parseAuditListSnapshot,
  peekAuditExpectRestore,
  saveAuditListSnapshot,
  type AuditListSnapshot
} from './audit-list-state.storage';

/**
 * Audit persona component
 * Extends BaseTabPersonaComponent with custom criteria-based search filters
 * instead of user/role-based passport loading.
 * Provides read-only access to all passports matching search criteria.
 */
@Component({
  selector: 'app-audit',
  standalone: true,
  imports: [
    HeaderComponent,
    AuditPassportCellComponent,
    CommonModule,
    NavbarComponent,
    LoadingSpinnerComponent,
    DateWithOrdinalPipe,
    NoPassportsComponent,
    FormsModule,
    PassportsDesktopTableComponent
  ],
  templateUrl: './audit.component.html',
  styleUrl: './audit.component.css'
})
export class AuditComponent extends BaseTabPersonaComponent implements OnInit, OnDestroy {
  protected get personaName(): string {
    return 'audit';
  }

  protected get defaultPersona(): string {
    return 'audit';
  }

  // Audit-specific filter form fields
  filterScheduledDate = '';
  filterLoadNumber = '';
  filterCropYear: string | number = '';  // Can be string or number from number input
  filterVendorNumber = '';
  filterDestination = '';
  filterVariety = '';
  filtersCollapsed = false;
  hasSearched = false;
  /** Total filtered rows on the server (across all pages); used to compute pagination. */
  totalRecords = 0;
  /** Shown when audit search API fails (timeout, network, etc.); not used for empty successful results. */
  readonly auditSearchError = signal<string | null>(null);

  constructor(
    http: HttpClient,
    searchService: SearchService,
    filterService: FilterService,
    passports: PassportService,
    ui: UiStateService,
    detailsStore: PassportDetailsStore,
    router: Router,
    offlineStorage: OfflineStorageService,
    commentsNotification: CommentsNotificationService,
    helper: PassportHelperService,
    personaRouting: PersonaRoutingService,
    private readonly commentsPanelService: CommentsPanelService,
    private readonly historyPanelService: HistoryPanelService,
    private readonly auditSnapshotBridge: AuditListSnapshotBridgeService
  ) {
    super(http, searchService, filterService, passports, ui, detailsStore, router, offlineStorage, commentsNotification, helper, personaRouting);
  }

  override ngOnInit(): void {
    this.auditSnapshotBridge.registerSaveHandler((passportId: string) => {
      this.persistAuditListSnapshotIfNeeded(passportId);
      this.ui.setExpandedRecord(passportId);
    });
    this.personaRouting.syncLastPersonaRouteFromRouter();
    this.updateDesktopViewState(true);

    const expectRestore = peekAuditExpectRestore();
    let snapshot = expectRestore ? parseAuditListSnapshot() : null;
    if (expectRestore && !snapshot) {
      clearAuditListRestoreState();
      snapshot = null;
    }

    const restoring = Boolean(expectRestore && snapshot);

    if (restoring && snapshot) {
      const snap = snapshot;
      this.applyAuditSnapshot(snap, 'beforeLoad');
      void (async () => {
        if (this.hasFilterValues) {
          await this.loadPassportsFromAPI();
        }
        this.applyAuditSnapshot(snap, 'afterLoad');
        this.clampAuditCurrentPageAfterRestore();
        this.ui.setExpandedRecord(snap.passportId);
        clearAuditListRestoreState();
        this.scrollPositionService.restoreScrollPositionAfterDataLoad('/audit');
        this.scrollRestoredPassportIntoView(snap.passportId);
      })();
      return;
    }

    // Fresh entry: clear session snapshot so Profile → Audit starts clean
    clearAuditListRestoreState();

    // Fresh entry: clear everything so no bleed from any other persona
    this.filterService.clear();
    this.searchService.clear();
    this.ui.setSearchOpen(false);
    this.ui.setFiltersOpen(false);
    this.ui.setSchedulerTabSelected('all');

    // Flush stale records AND details that may have been loaded by a previous persona
    this.passports.records.set([]);
    this.detailsStore.clear();

    // Reset audit-specific state
    this.filters = [];
    this.filterScheduledDate = '';
    this.filterLoadNumber = '';
    this.filterCropYear = '';
    this.filterVendorNumber = '';
    this.filterDestination = '';
    this.filterVariety = '';
    this.hasSearched = false;
    this.filtersCollapsed = false;
    this.currentPage = 1;
    this.totalRecords = 0;
  }

  ngOnDestroy(): void {
    this.auditSnapshotBridge.unregister();
  }

  /**
   * Persists list state when opening passport details after a search with at least one result.
   * Avoids `hasFilterValues` alone, which can disagree with fields the user already searched with.
   */
  private persistAuditListSnapshotIfNeeded(passportId: string): void {
    if (this.hasSearched && this.passports.records().length > 0) {
      saveAuditListSnapshot(this.buildAuditListSnapshot(passportId));
    }
  }

  private clampAuditCurrentPageAfterRestore(): void {
    const totalPages = this.totalPages;
    if (this.currentPage > totalPages) {
      this.currentPage = Math.max(1, totalPages);
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  private scrollRestoredPassportIntoView(passportId: string): void {
    if (globalThis.document === undefined) {
      return;
    }
    const safeId = passportId.replaceAll('"', '');
    requestAnimationFrame(() => {
      const candidates = globalThis.document.querySelectorAll(`[data-passport-id="${safeId}"]`);
      const el = Array.from(candidates).find(e => (e as HTMLElement).offsetParent !== null);
      el?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }

  private applyAuditSnapshot(snapshot: AuditListSnapshot, phase: 'beforeLoad' | 'afterLoad'): void {
    if (phase === 'beforeLoad') {
      this.filterScheduledDate = snapshot.filterScheduledDate;
      this.filterLoadNumber = snapshot.filterLoadNumber;
      this.filterCropYear = snapshot.filterCropYear;
      this.filterVendorNumber = snapshot.filterVendorNumber;
      this.filterDestination = snapshot.filterDestination;
      this.filterVariety = snapshot.filterVariety;
      this.hasSearched = snapshot.hasSearched;
      this.filtersCollapsed = snapshot.filtersCollapsed;
      this.sortColumn = snapshot.sortColumn;
      this.sortDirection = snapshot.sortDirection;
      this.currentPage = Number.isFinite(snapshot.currentPage) && snapshot.currentPage >= 1 ? snapshot.currentPage : 1;
      this.itemsPerPage = normalizeAuditDesktopShowRows(snapshot.itemsPerPage);
      return;
    }

    this.searchService.setQuery(snapshot.searchQuery);
    this.filterService.clear();
    for (const [filterId, values] of Object.entries(snapshot.filters)) {
      this.filterService.setFilter(filterId, values);
    }
    this.sortColumn = snapshot.sortColumn;
    this.sortDirection = snapshot.sortDirection;
    this.currentPage = Number.isFinite(snapshot.currentPage) && snapshot.currentPage >= 1 ? snapshot.currentPage : 1;
    this.itemsPerPage = normalizeAuditDesktopShowRows(snapshot.itemsPerPage);
    this.filterScheduledDate = snapshot.filterScheduledDate;
    this.filterLoadNumber = snapshot.filterLoadNumber;
    this.filterCropYear = snapshot.filterCropYear;
    this.filterVendorNumber = snapshot.filterVendorNumber;
    this.filterDestination = snapshot.filterDestination;
    this.filterVariety = snapshot.filterVariety;
    this.hasSearched = snapshot.hasSearched;
    this.filtersCollapsed = snapshot.filtersCollapsed;
  }

  private buildAuditListSnapshot(passportId: string): AuditListSnapshot {
    const filters = { ...this.filterService.filters() };
    const scrollY =
      globalThis.window === undefined
        ? undefined
        : globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0;

    return {
      filterScheduledDate: this.filterScheduledDate,
      filterLoadNumber: this.filterLoadNumber,
      filterCropYear: this.filterCropYear,
      filterVendorNumber: this.filterVendorNumber,
      filterDestination: this.filterDestination,
      filterVariety: this.filterVariety,
      hasSearched: this.hasSearched,
      filtersCollapsed: this.filtersCollapsed,
      filters,
      searchQuery: this.searchService.query(),
      currentPage: this.currentPage,
      itemsPerPage: normalizeAuditDesktopShowRows(this.itemsPerPage),
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      passportId,
      scrollY: scrollY !== undefined && scrollY > 0 ? scrollY : undefined
    };
  }

  /**
   * Check if at least one filter has a value
   */
  get hasFilterValues(): boolean {
    // Handle filterCropYear separately since it's a number input (could be 0, null, undefined, or empty string)
    const hasCropYear = this.filterCropYear !== null && this.filterCropYear !== undefined && this.filterCropYear !== '' && !Number.isNaN(Number(this.filterCropYear));
    return !!(
      this.filterScheduledDate ||
      this.filterLoadNumber ||
      hasCropYear ||
      this.filterVendorNumber ||
      this.filterDestination ||
      this.filterVariety
    );
  }

  /**
   * Execute audit search with current filter values.
   * Always resets to the first page so a new search starts from the top of the result set.
   */
  async onAuditSearch(): Promise<void> {
    if (!this.hasFilterValues) return;
    this.auditSearchError.set(null);
    this.hasSearched = true;
    this.currentPage = 1;
    await this.loadPassportsFromAPI();
  }

  /**
   * Override pagination handler so each page change re-fetches one server-side page
   * instead of slicing locally.  Avoids loading thousands of rows into memory.
   * Matches the base signature (returns void) by firing the async load without awaiting.
   */
  override onPageChange(page: number): void {
    if (page < 1) return;
    if (page === this.currentPage) return;
    this.currentPage = page;
    if (this.hasFilterValues && this.hasSearched) {
      void this.loadPassportsFromAPI();
    }
  }

  /**
   * Override rows-per-page handler so changing page size resets to page 1 and re-fetches.
   */
  override setItemsPerPage(count: number): void {
    if (!Number.isFinite(count) || count < 1) return;
    this.itemsPerPage = count;
    this.currentPage = 1;
    this.rowsDropdownOpen = false;
    if (this.hasFilterValues && this.hasSearched) {
      void this.loadPassportsFromAPI();
    }
  }

  /**
   * Clear all audit filter fields and results
   */
  onClearFilters(): void {
    this.filterScheduledDate = '';
    this.filterLoadNumber = '';
    this.filterCropYear = '';
    this.filterVendorNumber = '';
    this.filterDestination = '';
    this.filterVariety = '';
    this.auditSearchError.set(null);
    this.hasSearched = false;
    this.passports.records.set([]);
    this.totalRecords = 0;
  }

  /**
   * Toggle filter panel visibility on mobile
   */
  toggleFilters(): void {
    this.filtersCollapsed = !this.filtersCollapsed;
  }

  /**
   * Override API load for audit persona - calls audit search endpoint
   */
  protected override async loadPassportsFromAPI(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.auditSearchError.set(null);
    try {
      const { passports, totalCount, errorMessage } = await this.fetchAuditPassports();

      if (errorMessage) {
        this.auditSearchError.set(errorMessage);
        this.passports.records.set([]);
        this.totalRecords = 0;
        this.rebuildFilters();
        return;
      }

      this.totalRecords = totalCount;

      if (!passports || passports.length === 0) {
        this.passports.records.set([]);
        this.rebuildFilters();
        return;
      }

      // Map passports to frontend records with tab category
      const frontendRecords: PassportRecord[] = passports.map((p: any) => {
        const tabCategory = this.helper.mapPassportToTabCategory(
          p.statusCode,
          p.shuntingYardID,
          p.transloadingStationID,
          p.loadStatus
        );
        return {
          id: p.passportID.toString(),
          status: 'pending',
          isSynced: true,
          tabCategory,
          statusCode: p.statusCode || ''
        };
      });
      this.passports.records.set(frontendRecords);

      // Store details
      for (const p of passports) {
        this.detailsStore.upsert(this.buildPassportDetails(p));
      }

      this.rebuildFilters();
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Fetch passports from the audit search API endpoint.
   * Handles token retrieval, parameter building, and HTTP errors.
   */
  private async fetchAuditPassports(): Promise<{ passports: any[]; totalCount: number; errorMessage: string | null }> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      console.warn('No access token found for audit');
      return { passports: [], totalCount: 0, errorMessage: 'No access token found. Please sign in again.' };
    }
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    const params = this.buildAuditSearchParams();
    const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/audit/search?${params.toString()}`;
    const headers: Record<string, string> = {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
      'ngsw-bypass': 'true',
    };
    console.log(`📋 Audit searching passports with filters:`, Object.fromEntries(params));
    try {
      const data = await firstValueFrom(this.http.get<{ items?: any[]; totalCount?: number } | any[]>(apiUrl, { headers }));
      return this.normalizeAuditResponse(data);
    } catch (e: unknown) {
      console.warn('API call failed for audit search', e);
      return { passports: [], totalCount: 0, errorMessage: this.mapAuditSearchHttpError(e) };
    }
  }

  /**
   * Accept either the new paged shape `{ items, totalCount, ... }` (current backend)
   * or a legacy flat array (older deployments) so callers don't have to branch.
   */
  private normalizeAuditResponse(data: { items?: any[]; totalCount?: number } | any[] | null | undefined):
    { passports: any[]; totalCount: number; errorMessage: null } {
    if (Array.isArray(data)) {
      return { passports: data, totalCount: data.length, errorMessage: null };
    }
    const items = Array.isArray(data?.items) ? data.items : [];
    const totalCount = typeof data?.totalCount === 'number' ? data.totalCount : items.length;
    return { passports: items, totalCount, errorMessage: null };
  }

  private mapAuditSearchHttpError(e: unknown): string {
    if (e instanceof HttpErrorResponse) {
      if (e.status === 504) {
        return 'Search timed out. Please try again or narrow your filters.';
      }
      if (e.status === 0) {
        return 'Network error. Check your connection and try again.';
      }
      if (e.status === 401) {
        return 'Session expired. Please sign in again.';
      }
      if (e.status === 403) {
        return 'You do not have permission to run this search.';
      }
    }
    return 'Search could not be completed. Please try again.';
  }

  /**
   * Build URLSearchParams from the current audit filter field values.
   * Includes pageNumber/pageSize so the backend returns a single page rather than
   * the full result set.
   */
  private buildAuditSearchParams(): URLSearchParams {
    const params = new URLSearchParams();
    if (this.filterScheduledDate) params.set('scheduledDate', this.filterScheduledDate);
    if (this.filterLoadNumber) params.set('loadNumber', this.filterLoadNumber);
    this.appendCropYearParam(params);
    if (this.filterVendorNumber) params.set('vendorNumber', this.filterVendorNumber);
    if (this.filterDestination) params.set('destination', this.filterDestination);
    if (this.filterVariety) params.set('variety', this.filterVariety);
    params.set('pageNumber', String(this.currentPage > 0 ? this.currentPage : 1));
    params.set('pageSize', String(this.itemsPerPage > 0 ? this.itemsPerPage : 250));
    return params;
  }

  /**
   * Append the crop year as a seasonCode param (e.g. 2023 -> CY23) if valid.
   */
  private appendCropYearParam(params: URLSearchParams): void {
    if (this.filterCropYear === null || this.filterCropYear === undefined || this.filterCropYear === '') return;
    const yearStr = String(this.filterCropYear).trim();
    if (!yearStr || Number.isNaN(Number(yearStr))) return;
    // If user enters 2-digit year (e.g. 20), use as-is; if 4-digit (e.g. 2020), take last 2 digits
    const lastTwo = yearStr.length <= 2 ? yearStr.padStart(2, '0') : yearStr.slice(-2);
    params.set('seasonCode', `CY${lastTwo}`);
  }

  /**
   * Build passport details object from API passport data
   */
  private buildPassportDetails(p: any): any {
    return {
      id: p.passportID.toString(),
      loadNumber: p.loadNumber || '',
      vendorName: p.vendorName || '',
      vendorNumber: p.vendorNumber || '',
      apn: p.apn || '',
      apnName: p.apnName || '',
      destination: p.destination || '',
      destinationWeighbridge: p.destinationWeighbridge || '',
      origin: this.helper.formatOriginWithType(p.originName, p.rawSourceType),
      bulkerNumber: p.trailerNumber || '',
      bulkerMain: p.trailerMain || '',
      dueDateTime: this.dateTimeHelper.ensureUtcTimestamp(p.dueDateTime || p.scheduledDateTime || ''),
      haulier: p.haulierName || '',
      plantID: p.plantID || p.PlantID,
      statusCode: p.statusCode || '',
      variety: p.variety || '',
      shuntingYardID: p.shuntingYardID ?? null,
      transloadingStationID: p.transloadingStationID ?? null,
      isPassportCompleted: p.isPassportCompleted ?? false,
      isCancelled: (p.loadStatusName || '').toLowerCase() === 'cancelled',
      hasSeenPcrChanges: p.hasSeenPcrChanges || false,
      loadStatusName: p.loadStatusName || '',
      loadStatus: p.loadStatus || ''
    };
  }

  /**
   * Override to disable offline caching - Audit does not support offline
   */
  protected override async cacheOfflineData(headers: any): Promise<void> {
    return;
  }

  protected override rebuildFilters(): void {
    try {
      const records = this.passports.records();
      const originOptions: string[] = [];
      const destinationOptions: string[] = [];
      const apnOptions: string[] = [];

      for (const rec of records) {
        const d = this.detailsStore.get(rec.id);
        if (!d) continue;
        if (d.origin?.trim()) {
          this.helper.addUniqueOption(originOptions, d.origin);
        }
        const destinationValue = d.destinationWeighbridge || d.destination;
        this.helper.addUniqueOption(destinationOptions, destinationValue);
        const apnValue = d.apnName || d.apn;
        this.helper.addUniqueOption(apnOptions, apnValue);
      }

      this.filters = [
        this.helper.createFilterDefinition('origin', 'Origin', originOptions),
        this.helper.createFilterDefinition('destination', 'Destination', destinationOptions),
        this.helper.createFilterDefinition('apnName', 'APN/Name', apnOptions)
      ];
    } catch (err) {
      console.warn('Error rebuilding filters:', err);
      this.filters = this.helper.getDefaultOriginDestinationApnFilters();
    }
  }

  // Desktop View Getters & Methods

  override get visibleRecords(): PassportRecord[] {
    if (this.isDesktopView) {
      return this.getDesktopVisibleRecords();
    }
    // Mobile path: use local filter/search logic instead of super.visibleRecords
    // to avoid the base class tab-based filtering (schedulerTabSelected) which
    // could silently filter out audit results due to stale state from other personas.
    const records = this.getMobileFilteredRecords();
    // Sort mobile records by Scheduled Delivery Date & Time ascending
    return records.sort((a, b) => {
      const dateA = this.detailsStore.get(a.id)?.dueDateTime || '';
      const dateB = this.detailsStore.get(b.id)?.dueDateTime || '';
      return dateA.localeCompare(dateB);
    });
  }

  private getDesktopVisibleRecords(): PassportRecord[] {
    // Audit does not use status tabs (hideStatusTabs=true), so return all
    // filtered records without any tab-based filtering. This prevents stale
    // schedulerTabSelected state from silently hiding results.
    return this.getDesktopFilteredRecords();
  }

  private applyFiltersAndSearch(records: PassportRecord[], isDesktop: boolean): PassportRecord[] {
    try {
      const query = (this.searchService.query() || '').trim().toLowerCase();
      const hasQuery = query.length > 0;
      const activeFilters = this.filterService.filters();

      return records.filter(rec => {
        const det = this.detailsStore.get(rec.id);

        const apnValue = det?.apnName || det?.apn;
        if (!this.helper.matchesFilter(activeFilters, 'apnName', apnValue)) return false;
        if (!this.helper.matchesFilter(activeFilters, 'haulier', det?.haulier)) return false;
        const destinationValue = det?.destinationWeighbridge || det?.destination;
        if (!this.helper.matchesFilter(activeFilters, 'destination', destinationValue)) return false;
        if (!this.helper.matchesFilter(activeFilters, 'origin', det?.origin)) return false;

        if (!hasQuery) return true;

        const baseFields = this.helper.buildSearchFields(rec, det);
        if (isDesktop) {
          return this.buildDesktopSearchHaystack(baseFields, det).includes(query);
        } else {
          const haystack = baseFields.join(' ').toLowerCase();
          return haystack.includes(query);
        }
      });
    } catch {
      return [];
    }
  }

  private getDesktopFilteredRecords(): PassportRecord[] {
    return this.applyFiltersAndSearch(this.passports.records(), true);
  }

  private getMobileFilteredRecords(): PassportRecord[] {
    return this.applyFiltersAndSearch(this.passports.records(), false);
  }

  override get allCount(): number {
    if (this.isDesktopView) return this.getDesktopFilteredRecords().length;
    return this.getMobileFilteredRecords().length;
  }

  override get loadCount(): number {
    if (this.isDesktopView) return this.getDesktopFilteredRecords().filter(r => r.tabCategory === 'load').length;
    return this.getMobileFilteredRecords().filter(r => r.tabCategory === 'load').length;
  }

  override get shuntingCount(): number {
    if (this.isDesktopView) return this.getDesktopFilteredRecords().filter(r => r.tabCategory === 'shunting').length;
    return this.getMobileFilteredRecords().filter(r => r.tabCategory === 'shunting').length;
  }

  override get transloadingCount(): number {
    if (this.isDesktopView) return this.getDesktopFilteredRecords().filter(r => r.tabCategory === 'transloading').length;
    return this.getMobileFilteredRecords().filter(r => r.tabCategory === 'transloading').length;
  }

  get receivingCount(): number {
    if (this.isDesktopView) return this.getDesktopFilteredRecords().filter(r => r.tabCategory === 'receiving').length;
    return this.getMobileFilteredRecords().filter(r => r.tabCategory === 'receiving').length;
  }

  /**
   * Total pages is driven by the server-side filtered total, NOT by the count of
   * records currently in memory (which only ever holds the current page).
   */
  get totalPages(): number {
    if (!this.totalRecords || this.itemsPerPage <= 0) return 1;
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  /**
   * Audit uses server-side pagination, so the records already in memory ARE the
   * current page. Apply local search/filter/sort but do NOT slice again — the base
   * class would otherwise treat `currentPage > 1` as offset 250+ and render zero rows.
   */
  override get sortedPaginatedRecords(): PassportRecord[] {
    const records = [...this.visibleRecords];
    if (!this.sortColumn) return records;
    return records.sort((a, b) => {
      const aDetails = this.detailsStore.get(a.id);
      const bDetails = this.detailsStore.get(b.id);
      const { valA, valB } = this.getSortValues(this.sortColumn, a, b, aDetails, bDetails);
      return this.compareValues(valA, valB, this.sortDirection);
    });
  }

  openPassportDetails(id: string): void {
    this.ui.setExpandedRecord(id);
    this.persistAuditListSnapshotIfNeeded(id);
    this.scrollPositionService.saveScrollPosition('/audit');
    this.scrollPositionService.setReturningFromDetail(false);
    void this.router.navigate(['/passport', id], { state: { from: 'audit', tab: 'all' } });
  }

  private buildDesktopSearchHaystack(baseFields: string[], det: any): string {
    const dueDateTime = det?.dueDateTime || '';
    const formattedScheduled = dueDateTime
      ? this.dateTimeHelper.formatScheduledText(dueDateTime)
      : undefined;
    const scheduledText = formattedScheduled ? formattedScheduled.toLowerCase() : '';
    const combinedVendor = `${det?.vendorNumber || ''} - ${det?.vendorName || ''}`.toLowerCase();
    const statusText = (det?.statusCode || '').toLowerCase().replaceAll('_', ' ');

    return [
      ...baseFields,
      scheduledText,
      combinedVendor,
      statusText,
      det?.vendorNumber || '',
      det?.vendorName || '',
      det?.apn || '',
      det?.apnName || '',
      det?.bulkerMain || ''
    ].join(' ').toLowerCase();
  }

  protected override buildPanelHeader(id: string): { loadNumber: string; variety: string; haulier: string; scheduledText: string; origin: string; destination: string; apnName: string; vendorName: string; vendorNumber: string; bulkerNumber: string; isAudit?: boolean } | undefined {
    const header = super.buildPanelHeader(id);
    if (header) {
      return { ...header, isAudit: true };
    }
    return undefined;
  }

  override openComments(id: string, commentsPanelService?: CommentsPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      (commentsPanelService || this.commentsPanelService).openCommentsPanel(id, header, true);
    }
  }

  override openHistory(id: string, historyPanelService?: HistoryPanelService): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      (historyPanelService || this.historyPanelService).openHistoryPanel(id, header);
    }
  }
}
